import { useMutation, useQuery } from '@apollo/client';
import { faFileArrowDown } from '@fortawesome/pro-regular-svg-icons';
import { camelCase } from 'lodash';
import * as React from 'react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import ChangeCards, { ChangeReviewModal } from './ChangeCards.tsx';
import { ConfigChannel, toConfigChanges } from './changes.ts';
import {
    CHANNEL_CONFIG_REFETCH_QUERIES,
    EXPORT_CHANNEL_CONFIG,
    GET_CHANNEL_CONFIG_VERSION_DIFF,
    RESTORE_CHANNEL_CONFIG_VERSION,
} from './queries.ts';
import Button from '../../../core/components/Button.tsx';
import Checkbox from '../../../core/components/Checkbox.tsx';
import { withIcon } from '../../../core/components/Icon.tsx';
import Modal from '../../../core/components/Modal.tsx';
import { useToaster } from '../../../core/components/Toast.tsx';

const DownloadIcon = withIcon(faFileArrowDown);

const MODAL_WIDTH = 'w-[40rem] max-w-full';

const useVersionDiff = (channel: ConfigChannel, fromNumber: number | null, toNumber: number | null) => {
    const { data, loading, error } = useQuery(GET_CHANNEL_CONFIG_VERSION_DIFF, {
        fetchPolicy: 'no-cache',
        variables: { documentType: channel.documentType, channelId: channel.channelId, fromNumber, toNumber },
    });
    const changes = useMemo(() => toConfigChanges(data?.channelConfigVersionDiff), [data]);
    return { changes, loading, error };
};

interface DiffStatusProps {
    isLoading: boolean;
}

const DiffStatus = ({ isLoading }: DiffStatusProps) => {
    const { t } = useTranslation('config');
    return (
        <span className="text-sm text-content-medium">
            {t(isLoading ? 'configPage.history.loadingChanges' : 'configPage.history.changesError')}
        </span>
    );
};

interface CompareVersionModalProps {
    channel: ConfigChannel;
    number: number;
    currentNumber: number;
    onRestore: () => void;
    onClose: () => void;
}

export const CompareVersionModal = ({
    channel,
    number,
    currentNumber,
    onRestore,
    onClose,
}: CompareVersionModalProps) => {
    const { t } = useTranslation('config');
    const { changes, loading, error } = useVersionDiff(channel, number, null);
    return (
        <Modal
            title={t('configPage.history.compare.title', { number })}
            onClose={onClose}
            buttons={
                <>
                    <Button onClick={onClose}>{t('configPage.history.compare.close')}</Button>
                    <Button onClick={onRestore}>{t('configPage.history.restore.action')}</Button>
                </>
            }
        >
            <div className={`flex flex-col gap-4 pb-2 ${MODAL_WIDTH}`}>
                <span className="text-sm text-content-medium">
                    {t('configPage.history.compare.description', { number, current: currentNumber })}
                </span>
                {loading || error ? (
                    <DiffStatus isLoading={loading} />
                ) : (
                    <ChangeCards
                        parts={channel.parts}
                        changes={changes}
                        empty={t('configPage.history.sameAsCurrent', { number })}
                    />
                )}
            </div>
        </Modal>
    );
};

interface RestoreVersionModalProps {
    channel: ConfigChannel;
    number: number;
    onClose: () => void;
}

export const RestoreVersionModal = ({ channel, number, onClose }: RestoreVersionModalProps) => {
    const { t } = useTranslation('config');
    const { publishToast } = useToaster();
    const { changes, loading, error } = useVersionDiff(channel, null, number);
    const [restore] = useMutation(RESTORE_CHANNEL_CONFIG_VERSION, { refetchQueries: CHANNEL_CONFIG_REFETCH_QUERIES });

    const handleSubmit = async (paths: string[]) => {
        const { data } = await restore({
            variables: { documentType: channel.documentType, channelId: channel.channelId, number, paths },
        });
        const skippedPaths: string[] = data?.restoreChannelConfigVersion?.skippedPaths ?? [];
        if (skippedPaths.length > 0) {
            publishToast({
                description: t('configPage.history.restore.skipped', { paths: skippedPaths.join(', ') }),
                status: 'warning',
            });
        }
        channel.onConfigChange();
        return t('configPage.history.restore.success', { count: paths.length - skippedPaths.length });
    };

    return (
        <ChangeReviewModal
            title={t('configPage.history.restore.title', { number })}
            description={t('configPage.history.restore.description', { number })}
            parts={channel.parts}
            changes={changes}
            status={loading || error ? <DiffStatus isLoading={loading} /> : undefined}
            empty={t('configPage.history.sameAsCurrent', { number })}
            submitKey="configPage.history.restore.submit"
            onSubmit={handleSubmit}
            onClose={onClose}
        />
    );
};

const downloadFile = (data: string, fileName: string) => {
    const url = window.URL.createObjectURL(new Blob([data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(link);
};

interface DownloadVersionModalProps {
    channel: ConfigChannel;
    number: number;
    onClose: () => void;
}

export const DownloadVersionModal = ({ channel, number, onClose }: DownloadVersionModalProps) => {
    const { t } = useTranslation('config');
    const { publishToast } = useToaster();
    const allOptions = channel.parts.map((part) => part.option);
    const [selected, setSelected] = useState<string[]>(allOptions);
    const [exportConfig, { loading }] = useMutation(EXPORT_CHANNEL_CONFIG);
    const fileName = `${channel.channelName} v${number}.json`;
    const isAll = selected.length === allOptions.length;

    const toggle = (option: string) =>
        setSelected((current) =>
            current.includes(option) ? current.filter((value) => value !== option) : [...current, option]
        );

    const handleDownload = async () => {
        try {
            const { data } = await exportConfig({
                variables: {
                    documentType: channel.documentType,
                    channelId: channel.channelId,
                    number,
                    sections: channel.parts
                        .filter((part) => selected.includes(part.option))
                        .flatMap((part) => part.sections),
                },
            });
            downloadFile(data.exportChannelConfig.data, fileName);
            publishToast({ description: t('configPage.history.download.success', { fileName }), status: 'success' });
            onClose();
        } catch (error) {
            console.error(error);
            publishToast({ description: t('configPage.history.error'), status: 'error' });
        }
    };

    return (
        <Modal
            title={t('configPage.history.download.title', { number })}
            onClose={onClose}
            buttons={
                <>
                    <Button onClick={onClose}>{t('configPage.history.cancel')}</Button>
                    <Button
                        variant="primary"
                        className="flex items-center gap-2"
                        disabled={selected.length === 0}
                        loading={loading}
                        onClick={handleDownload}
                    >
                        <DownloadIcon />
                        {isAll
                            ? t('configPage.history.download.submit')
                            : t('configPage.history.download.submitParts', { count: selected.length })}
                    </Button>
                </>
            }
        >
            <div className={`flex flex-col gap-4 pb-2 ${MODAL_WIDTH}`}>
                <span className="text-sm text-content-medium">
                    {t('configPage.history.download.fileLine', { fileName })}
                </span>
                <div className="flex flex-col overflow-hidden rounded-lg border border-solid border-line-weak">
                    <label className="flex cursor-pointer items-center gap-3 bg-surface-medium px-4 py-2">
                        <Checkbox
                            checked={isAll}
                            indeterminate={selected.length > 0 && !isAll}
                            onChange={() => setSelected(isAll ? [] : allOptions)}
                        />
                        <span className="flex-1 text-sm font-semibold text-content-strong">
                            {t('configPage.history.download.allParts')}
                        </span>
                        <span className="text-xs text-content-medium">
                            {t('configPage.history.download.partCount', {
                                count: selected.length,
                                total: allOptions.length,
                            })}
                        </span>
                    </label>
                    {channel.parts.map((part) => (
                        <label
                            key={part.option}
                            className="flex cursor-pointer items-start gap-3 border-0 border-t border-solid border-line-weak px-4 py-2"
                        >
                            <Checkbox
                                className="mt-0.5"
                                checked={selected.includes(part.option)}
                                onChange={() => toggle(part.option)}
                            />
                            <span className="flex min-w-0 flex-col">
                                <span className="text-sm text-content-strong">{part.label}</span>
                                <span className="text-xs text-content-medium">
                                    {t(`configPage.history.download.parts.${camelCase(part.option)}`, {
                                        defaultValue: '',
                                    })}
                                </span>
                            </span>
                        </label>
                    ))}
                </div>
            </div>
        </Modal>
    );
};
